import { useState, useEffect, useRef } from 'react';
import { Trash2, GripVertical } from 'lucide-react';
import { rpc, formatTime } from '@/api/client';
import { Dialog } from '@/components/common/Dialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';

// ─── Types matching the import dialog shape ────────────────────────────

interface ParsedSeason { id: string; name: string; year: number; country: string; }
interface ParsedCategory { id: string; name: string; slug: string; }
interface ParsedEvent { id: string; roundNumber: number; trackName: string; country: string; date: string; sourcePdf: string; }
interface ParsedSessionMeta { id: string; sessionType: string; sessionLabel: string; totalLaps: number; time: string; }
interface ParsedDriver { firstName: string; lastName: string; nationality: string; }
interface ParsedEntry {
  driver: ParsedDriver;
  kartNumber: number;
  gridPosition: number;
  finishPosition: number;
  bestLapMs: number;
  totalTimeMs: number;
  lapsCompleted: number;
  penaltySeconds: number;
  penaltyReason: string;
  status: string;
}

interface ParsedRaceSession {
  season: ParsedSeason;
  category: ParsedCategory;
  event: ParsedEvent;
  session: ParsedSessionMeta;
  entries: ParsedEntry[];
}

// ─── Session type options ──────────────────────────────────────────────
const SESSION_TYPES = [
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'heat-1', label: 'Heat 1' },
  { value: 'heat-2', label: 'Heat 2' },
  { value: 'heat-3', label: 'Heat 3' },
  { value: 'prefinal', label: 'Pre-Final' },
  { value: 'prefinal-a', label: 'Pre-Final A' },
  { value: 'prefinal-b', label: 'Pre-Final B' },
  { value: 'final', label: 'Final' },
];

// ─── Props ─────────────────────────────────────────────────────────────

interface Props {
  session: { id: string; sessionLabel: string; trackName: string; date: string };
  onClose: () => void;
  onSaved?: () => void;
}

export function RaceSessionDetailDialog({ session, onClose, onSaved }: Props) {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [data, setData] = useState<ParsedRaceSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Race tracks (for track dropdown) ────────────────────────────
  interface RaceTrackOption { id: string; name: string; }
  const [raceTracks, setRaceTracks] = useState<RaceTrackOption[]>([]);

  useEffect(() => {
    rpc<{ tracks?: RaceTrackOption[] } | RaceTrackOption[]>('ListRaceTracks', { page: 1, pageSize: 100 })
      .then(res => setRaceTracks(Array.isArray(res) ? res : (res?.tracks ?? [])))
      .catch(() => setRaceTracks([]));
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const result = await rpc<ParsedRaceSession>('GetEditorRaceSession', { id: session.id });
        setData(result);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [session.id]);

  // ─── Field update helpers ────────────────────────────────────────

  const updateField = (path: string, value: string | number) => {
    setData(prev => {
      if (!prev) return prev;
      const clone = structuredClone(prev);
      const parts = path.split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let obj: any = clone;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const updateEntryField = (entryIndex: number, path: string, value: string | number) => {
    setData(prev => {
      if (!prev) return prev;
      const clone = structuredClone(prev);
      const parts = path.split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let obj: any = clone.entries[entryIndex];
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const removeEntry = (entryIndex: number) => {
    setData(prev => {
      if (!prev) return prev;
      const clone = structuredClone(prev);
      clone.entries.splice(entryIndex, 1);
      return clone;
    });
  };

  // ─── Drag-and-drop reordering ────────────────────────────────────

  const dragEntryRef = useRef<{ index: number } | null>(null);

  const handleEntryDragStart = (index: number) => {
    dragEntryRef.current = { index };
  };

  const handleEntryDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragEntryRef.current) return;
    e.dataTransfer.dropEffect = 'move';
  };

  const handleEntryDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!dragEntryRef.current) return;
    const fromIndex = dragEntryRef.current.index;
    if (fromIndex === targetIndex) return;

    setData(prev => {
      if (!prev) return prev;
      const clone = structuredClone(prev);
      const [moved] = clone.entries.splice(fromIndex, 1);
      clone.entries.splice(targetIndex, 0, moved);
      clone.entries.forEach((entry, i) => { entry.finishPosition = i + 1; });
      return clone;
    });
    dragEntryRef.current = null;
  };

  const handleEntryDragEnd = () => {
    dragEntryRef.current = null;
  };

  // ─── Save handler ────────────────────────────────────────────────

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      // Auto-assign finishPosition from array order
      const clone = structuredClone(data);
      clone.entries.forEach((entry, i) => { entry.finishPosition = i + 1; });

      await rpc('SubmitRaceSessions', {
        sessions: [{
          season: clone.season,
          category: clone.category,
          event: clone.event,
          session: clone.session,
          entries: clone.entries,
        }],
      });
      showToast(t('races.saved'));
      onSaved?.();
      onClose();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Footer ──────────────────────────────────────────────────────

  const footer = data ? (
    <>
      <button
        onClick={handleSave}
        disabled={saving}
        className={`inline-flex items-center gap-1 px-4 py-2 rounded-xs text-sm font-medium
          transition-all cursor-pointer bg-accent text-white
          ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {saving ? t('common.loading') : t('common.save')}
      </button>
      <button
        onClick={onClose}
        className="inline-flex items-center px-4 py-2 rounded-xs text-sm font-medium
          bg-transparent text-text-secondary border border-border-default
          hover:bg-white/10 hover:text-text-primary transition-colors cursor-pointer"
      >
        {t('common.cancel')}
      </button>
    </>
  ) : null;

  const subtitle = t('races.detailSubtitle', {
    type: session.sessionLabel,
    track: session.trackName,
    date: session.date,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('races.detailTitle')}
      subtitle={subtitle}
      footer={footer}
      preventClose={saving}
    >
      {loading ? (
        <LoadingSpinner message={t('races.loading')} />
      ) : error ? (
        <ErrorState message={t('races.loadFailed', { error })} />
      ) : data ? (
        <div className="space-y-3">
          {/* Season */}
          <FieldSection title={t('races.season')}>
            <FieldRow label={t('tracks.name')}>
              <input value={data.season.name} onChange={e => updateField('season.name', e.target.value)}
                className="editor-input" />
            </FieldRow>
            <FieldRow label={t('races.year')}>
              <input type="number" value={data.season.year} onChange={e => updateField('season.year', parseInt(e.target.value) || 0)}
                className="editor-input w-24" />
            </FieldRow>
            <FieldRow label={t('races.country')}>
              <input value={data.season.country} onChange={e => updateField('season.country', e.target.value)}
                className="editor-input w-20" />
            </FieldRow>
          </FieldSection>

          {/* Category */}
          <FieldSection title={t('races.category')}>
            <FieldRow label={t('tracks.name')}>
              <input value={data.category.name} onChange={e => updateField('category.name', e.target.value)}
                className="editor-input" />
            </FieldRow>
          </FieldSection>

          {/* Event */}
          <FieldSection title={t('races.event')}>
            <FieldRow label={t('races.track')}>
              <select value={data.event.trackName} onChange={e => updateField('event.trackName', e.target.value)}
                className="editor-input">
                {/* Show current value if it doesn't match any known track */}
                {!raceTracks.some(t => t.name === data.event.trackName) && data.event.trackName && (
                  <option value={data.event.trackName}>{data.event.trackName}</option>
                )}
                {raceTracks.map(t => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label={t('races.roundLabel')}>
              <input type="number" value={data.event.roundNumber} onChange={e => updateField('event.roundNumber', parseInt(e.target.value) || 0)}
                className="editor-input w-20" />
            </FieldRow>
            <FieldRow label={t('races.date')}>
              <input type="date" value={data.event.date} onChange={e => updateField('event.date', e.target.value)}
                className="editor-input w-40" />
            </FieldRow>
          </FieldSection>

          {/* Session */}
          <FieldSection title={t('races.session')}>
            <FieldRow label={t('races.type')}>
              <select value={data.session.sessionType} onChange={e => updateField('session.sessionType', e.target.value)}
                className="editor-input w-40">
                {SESSION_TYPES.map(st => (
                  <option key={st.value} value={st.value}>{st.label}</option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label={t('races.label')}>
              <input value={data.session.sessionLabel} onChange={e => updateField('session.sessionLabel', e.target.value)}
                className="editor-input" />
            </FieldRow>
            <FieldRow label={t('races.time')}>
              <input value={data.session.time} onChange={e => updateField('session.time', e.target.value)}
                placeholder="HH:MM" className="editor-input w-24" />
            </FieldRow>
          </FieldSection>

          {/* Entries */}
          <FieldSection title={`${t('races.entries')} (${data.entries.length})`}>
            <div className="overflow-x-auto -mx-1">
              <table className="min-w-[1400px] text-[0.78rem] border-collapse">
                <thead>
                  <tr className="divider">
                    <th className="px-1 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-center w-8"></th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-center w-12">{t('races.position')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-left w-24">#</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-left min-w-[140px]">{t('races.firstName')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-left min-w-[160px]">{t('races.lastName')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-center w-20">{t('races.nationality')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-center w-20">{t('races.grid')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-left w-64">{t('races.bestLap')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-left w-64">{t('races.totalTime')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-center w-20">{t('races.laps')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-center w-20">{t('races.penalty')}</th>
                    <th className="px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted text-left w-24">{t('races.status')}</th>
                    <th className="px-1 py-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry, ei) => (
                    <tr
                      key={ei}
                      draggable
                      onDragStart={() => handleEntryDragStart(ei)}
                      onDragOver={handleEntryDragOver}
                      onDrop={e => handleEntryDrop(e, ei)}
                      onDragEnd={handleEntryDragEnd}
                      className={`hover:bg-bg-hover transition-colors cursor-grab active:cursor-grabbing ${ei < data.entries.length - 1 ? 'divider' : ''}`}
                    >
                      <td className="px-1 py-1 text-center">
                        <GripVertical className="w-3.5 h-3.5 text-text-muted inline-block" />
                      </td>
                      <td className="px-2 py-1 text-center tabular-nums text-text-muted font-medium">
                        {ei + 1}
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={entry.kartNumber}
                          onChange={e => updateEntryField(ei, 'kartNumber', parseInt(e.target.value) || 0)}
                          className="editor-input !px-2 !py-1 w-full text-center tabular-nums" />
                      </td>
                      <td className="px-2 py-1">
                        <input value={entry.driver.firstName}
                          onChange={e => updateEntryField(ei, 'driver.firstName', e.target.value)}
                          className="editor-input !px-2 !py-1 w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input value={entry.driver.lastName}
                          onChange={e => updateEntryField(ei, 'driver.lastName', e.target.value)}
                          className="editor-input !px-2 !py-1 w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input value={entry.driver.nationality}
                          onChange={e => updateEntryField(ei, 'driver.nationality', e.target.value)}
                          className="editor-input !px-2 !py-1 w-full text-center uppercase" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={entry.gridPosition}
                          onChange={e => updateEntryField(ei, 'gridPosition', parseInt(e.target.value) || 0)}
                          className="editor-input !px-2 !py-1 w-full text-center tabular-nums" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={entry.bestLapMs}
                          onChange={e => updateEntryField(ei, 'bestLapMs', parseInt(e.target.value) || 0)}
                          className="editor-input !px-2 !py-1 w-full text-center tabular-nums" />
                        <div className="text-[0.65rem] text-text-muted text-center mt-0.5 tabular-nums">
                          {formatTime(entry.bestLapMs)}
                        </div>
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={entry.totalTimeMs}
                          onChange={e => updateEntryField(ei, 'totalTimeMs', parseInt(e.target.value) || 0)}
                          className="editor-input !px-2 !py-1 w-full text-center tabular-nums" />
                        <div className="text-[0.65rem] text-text-muted text-center mt-0.5 tabular-nums">
                          {formatTime(entry.totalTimeMs)}
                        </div>
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={entry.lapsCompleted}
                          onChange={e => updateEntryField(ei, 'lapsCompleted', parseInt(e.target.value) || 0)}
                          className="editor-input !px-2 !py-1 w-full text-center tabular-nums" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={entry.penaltySeconds}
                          onChange={e => updateEntryField(ei, 'penaltySeconds', parseInt(e.target.value) || 0)}
                          className="editor-input !px-2 !py-1 w-full text-center tabular-nums"
                          title={entry.penaltyReason || undefined} />
                      </td>
                      <td className="px-2 py-1">
                        <select value={entry.status}
                          onChange={e => updateEntryField(ei, 'status', e.target.value)}
                          className="editor-input !px-2 !py-1 w-full">
                          <option value="finished">OK</option>
                          <option value="dnf">DNF</option>
                          <option value="dns">DNS</option>
                          <option value="dsq">DSQ</option>
                        </select>
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button
                          onClick={() => removeEntry(ei)}
                          className="text-text-muted hover:text-red transition-colors cursor-pointer p-1"
                          title={t('races.deleteEntry')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FieldSection>
        </div>
      ) : null}
    </Dialog>
  );
}

// ─── Helper components ──────────────────────────────────────────────────

function FieldSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[0.68rem] font-semibold tracking-wider uppercase text-text-muted mb-1.5">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[0.78rem] text-text-muted w-16 flex-shrink-0">{label}</span>
      {children}
    </div>
  );
}
